/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

// Console redesign primitives. Visual source: "Podstack Portal UX Redesign"
// canvas (Oct 2026). Styles live as .ps-* classes in index.css so non-React
// markup (and older pages) can use the same look.

import React from 'react';
import { Link, NavLink } from 'react-router-dom';

const cx = (...c: Array<string | false | null | undefined>) => c.filter(Boolean).join(' ');

/** Main content column: padding + max width used on every console screen. */
export const Page: React.FC<{ children: React.ReactNode; className?: string; wide?: boolean }> = ({
  children,
  className,
  wide,
}) => (
  <div
    className={cx(
      'w-full px-4 py-5 sm:px-7 sm:py-7 flex flex-col gap-5 sm:gap-6',
      wide ? 'max-w-[1320px]' : 'max-w-[1280px]',
      className,
    )}
  >
    {children}
  </div>
);

export const PageHeader: React.FC<{
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  back?: { to: string; label: string };
  eyebrow?: React.ReactNode;
}> = ({ title, subtitle, actions, back, eyebrow }) => (
  <div className="flex flex-wrap justify-between items-end gap-4">
    <div className="min-w-0 flex flex-col gap-1.5">
      {back && (
        <Link to={back.to} className="ps-link text-sm">
          ← {back.label}
        </Link>
      )}
      {eyebrow && <div className="ps-eyebrow">{eyebrow}</div>}
      <h1 className="m-0 text-2xl sm:text-[30px] font-medium text-ps-text">{title}</h1>
      {subtitle && <p className="m-0 text-ps-muted text-sm leading-[1.55] max-w-[720px]">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap items-center gap-2.5">{actions}</div>}
  </div>
);

export const Card: React.FC<React.HTMLAttributes<HTMLElement> & { as?: 'div' | 'section' | 'aside' }> = ({
  as = 'div',
  className,
  children,
  ...rest
}) => React.createElement(as, { className: cx('ps-card', className), ...rest }, children);

type BtnVariant = 'primary' | 'secondary' | 'danger';
const btnClass = (v: BtnVariant = 'primary', sm?: boolean) =>
  cx(v === 'primary' ? 'ps-btn' : v === 'danger' ? 'ps-btn-danger' : 'ps-btn2', sm && 'ps-btn-sm');

export const Button: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: BtnVariant; size?: 'sm' | 'md' }
> = ({ variant, size, className, type = 'button', ...rest }) => (
  <button type={type} className={cx(btnClass(variant, size === 'sm'), className)} {...rest} />
);

export const LinkButton: React.FC<{
  to: string;
  variant?: BtnVariant;
  size?: 'sm' | 'md';
  className?: string;
  children: React.ReactNode;
  external?: boolean;
}> = ({ to, variant, size, className, children, external }) =>
  external || /^https?:/.test(to) ? (
    <a href={to} target="_blank" rel="noopener noreferrer" className={cx(btnClass(variant, size === 'sm'), className)}>
      {children}
    </a>
  ) : (
    <Link to={to} className={cx(btnClass(variant, size === 'sm'), className)}>
      {children}
    </Link>
  );

export type Tone = 'ok' | 'warn' | 'err' | 'off' | 'info' | 'accent' | 'vid';
// Literal class names so Tailwind's content scan keeps every tone.
const TONE_CLASS: Record<Tone, string> = {
  ok: 'ps-tag-ok',
  warn: 'ps-tag-warn',
  err: 'ps-tag-err',
  off: 'ps-tag-off',
  info: 'ps-tag-info',
  accent: 'ps-tag-accent',
  vid: 'ps-tag-vid',
};
export const Tag: React.FC<{ tone?: Tone; className?: string; children: React.ReactNode }> = ({
  tone = 'off',
  className,
  children,
}) => <span className={cx('ps-tag', TONE_CLASS[tone], className)}>{children}</span>;

/** Map a backend status string to a tag tone. */
export const statusTone = (status?: string): Tone => {
  const s = (status || '').toLowerCase();
  if (['running', 'active', 'ready', 'healthy', 'succeeded', 'success', 'paid', 'completed', 'bound', 'available'].includes(s))
    return 'ok';
  if (['pending', 'creating', 'starting', 'provisioning', 'stopping', 'deleting', 'restarting', 'queued', 'processing', 'unpaid'].includes(s))
    return 'warn';
  if (['failed', 'error', 'crashloopbackoff', 'terminated_error', 'overdue', 'cancelled', 'canceled'].includes(s)) return 'err';
  return 'off';
};

export const StatusTag: React.FC<{ status?: string }> = ({ status }) => (
  <Tag tone={statusTone(status)}>
    {status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : 'Unknown'}
  </Tag>
);

export const IconBox: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <span className={cx('ps-icb', className)}>{children}</span>
);

/** Route-driven tabs (each tab is a link). */
export const Tabs: React.FC<{
  label: string;
  tabs: Array<{ label: React.ReactNode; to: string; end?: boolean; badge?: React.ReactNode }>;
}> = ({ label, tabs }) => (
  <nav className="flex flex-wrap border-b border-ps-border" aria-label={label}>
    {tabs.map((t) => (
      <NavLink key={t.to} to={t.to} end={t.end} className={({ isActive }) => cx('ps-tab', isActive && 'ps-tab-on')}>
        {t.label}
        {t.badge != null && <Tag tone="off">{t.badge}</Tag>}
      </NavLink>
    ))}
  </nav>
);

/** State-driven tabs (buttons) for in-page sections. */
export function SegmentTabs<T extends string>({
  label,
  value,
  onChange,
  tabs,
}: {
  label: string;
  value: T;
  onChange: (v: T) => void;
  tabs: Array<{ value: T; label: React.ReactNode; badge?: React.ReactNode }>;
}) {
  return (
    <div className="flex flex-wrap border-b border-ps-border" role="tablist" aria-label={label}>
      {tabs.map((t) => (
        <button
          key={t.value}
          type="button"
          role="tab"
          aria-selected={value === t.value}
          onClick={() => onChange(t.value)}
          className={cx('ps-tab bg-transparent', value === t.value && 'ps-tab-on')}
        >
          {t.label}
          {t.badge != null && <Tag tone="off">{t.badge}</Tag>}
        </button>
      ))}
    </div>
  );
}

export const Chip: React.FC<{ on?: boolean; onClick?: () => void; children: React.ReactNode }> = ({
  on,
  onClick,
  children,
}) => (
  <button type="button" aria-pressed={!!on} onClick={onClick} className={cx('ps-chip', on && 'ps-chip-on')}>
    {children}
  </button>
);

export const StatTile: React.FC<{
  label: React.ReactNode;
  value: React.ReactNode;
  sub?: React.ReactNode;
  icon?: React.ReactNode;
  to?: string;
}> = ({ label, value, sub, icon, to }) => {
  const body = (
    <>
      <div className="flex items-center justify-between gap-3">
        <span className="text-[13px] text-ps-muted">{label}</span>
        {icon && <span className="text-ps-subtle [&>svg]:w-[18px] [&>svg]:h-[18px]">{icon}</span>}
      </div>
      <div className="text-2xl font-semibold text-ps-text">{value}</div>
      {sub && <div className="text-xs text-ps-subtle">{sub}</div>}
    </>
  );
  const cls = 'ps-card p-[18px] flex flex-col gap-2 min-w-0';
  return to ? (
    <Link to={to} className={cx(cls, 'hover:border-ps-strong transition-colors')}>
      {body}
    </Link>
  ) : (
    <div className={cls}>{body}</div>
  );
};

export const SectionHeader: React.FC<{ title: React.ReactNode; id?: string; action?: React.ReactNode }> = ({
  title,
  id,
  action,
}) => (
  <div className="flex flex-wrap justify-between items-baseline gap-3">
    <h2 id={id} className="m-0 text-lg font-medium text-ps-text">
      {title}
    </h2>
    {action && <div className="text-sm">{action}</div>}
  </div>
);

export const EmptyState: React.FC<{
  icon?: React.ReactNode;
  title: React.ReactNode;
  body?: React.ReactNode;
  actions?: React.ReactNode;
}> = ({ icon, title, body, actions }) => (
  <div className="ps-card px-7 py-9 flex flex-col items-center text-center gap-3.5">
    {icon && <IconBox className="w-[52px] h-[52px] [&>svg]:w-6 [&>svg]:h-6">{icon}</IconBox>}
    <h2 className="m-0 text-xl font-medium text-ps-text">{title}</h2>
    {body && <p className="m-0 text-ps-muted text-[15px] max-w-[520px] leading-relaxed">{body}</p>}
    {actions && <div className="flex gap-2.5 flex-wrap justify-center">{actions}</div>}
  </div>
);

/** Card that hosts a table and scrolls it horizontally on narrow screens. */
export const TableCard: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <div className={cx('ps-card overflow-x-auto', className)}>
    <table className="ps-tbl">{children}</table>
  </div>
);

export const Stepper: React.FC<{ steps: string[]; current: number; onStep?: (i: number) => void }> = ({
  steps,
  current,
  onStep,
}) => (
  <ol className="list-none m-0 p-0 flex flex-wrap gap-x-7 gap-y-3" aria-label="Steps">
    {steps.map((s, i) => {
      const on = i === current;
      const done = i < current;
      const inner = (
        <>
          <b
            className={cx(
              'w-[26px] h-[26px] rounded-full grid place-items-center border text-[13px] font-semibold',
              on || done ? 'bg-ps-accent text-ps-chrome border-ps-accent' : 'border-ps-strong',
            )}
          >
            {done ? '✓' : i + 1}
          </b>
          {s}
        </>
      );
      return (
        <li
          key={s}
          aria-current={on ? 'step' : undefined}
          className={cx('flex items-center gap-2 text-sm', on ? 'text-ps-text' : 'text-ps-muted')}
        >
          {onStep && done ? (
            <button type="button" onClick={() => onStep(i)} className="flex items-center gap-2 hover:text-ps-text">
              {inner}
            </button>
          ) : (
            inner
          )}
        </li>
      );
    })}
  </ol>
);

/** Label/value row used in summary asides. */
export const KV: React.FC<{ k: React.ReactNode; v: React.ReactNode }> = ({ k, v }) => (
  <div className="flex justify-between gap-4 text-sm">
    <span className="text-ps-muted">{k}</span>
    <span className="text-right text-ps-text min-w-0 truncate">{v}</span>
  </div>
);

export const Skeleton: React.FC<{ className?: string }> = ({ className }) => (
  <div className={cx('animate-pulse rounded-md bg-ps-raised', className)} />
);

/** Section tabs shown under each Inference page header (artboard tab row). */
export const InferenceTabs: React.FC = () => (
  <Tabs
    label="Inference sections"
    tabs={[
      { label: 'Models', to: '/console/models' },
      { label: 'Playground', to: '/console/playground' },
      { label: 'API keys', to: '/console/keys' },
      { label: 'Usage', to: '/console/usage' },
      { label: 'Docs', to: '/console/docs' },
    ]}
  />
);

export const INFERENCE_EYEBROW = 'Inference · Serverless models';

export const formatINR = (n: number | undefined | null, currency: string = 'INR', digits = 2) => {
  const v = Number(n ?? 0);
  const sym = currency === 'USD' ? '$' : '₹';
  return `${v < 0 ? '−' : ''}${sym}${Math.abs(v).toLocaleString(currency === 'USD' ? 'en-US' : 'en-IN', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}`;
};
